import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { createAuthHeaders } from "../UserDashboard/useDashboardData";

const SAVE_DEBOUNCE_MS = 800;

const readLocal = (storageKey) => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
    if (saved && typeof saved === "object") return saved;
  } catch (e) {
    // uszkodzony zapis - użyj wartości domyślnych
  }
  return {};
};

/**
 * Widoczne kolumny i rozmiar strony użytkownika dla jednej tabeli.
 * Źródłem prawdy jest baza (GET/PUT /me/dashboard-preferences?view=...), dzięki czemu
 * ustawienia działają na każdym urządzeniu. localStorage jest tylko szybkim cache'em
 * i zapasem, gdy serwer nie odpowiada.
 *
 * `ready` mówi, że próba pobrania z serwera się zakończyła (udana lub nie) - strony
 * czekają na nie z pierwszym zapytaniem o dane, żeby nie pobierać ich dwa razy.
 */
export function useViewPreferences({
  view,
  storageKey,
  defaultHiddenColumns,
  pageSizeOptions,
  defaultPageSize,
}) {
  const initial = useRef(readLocal(storageKey)).current;
  const [hiddenColumns, setHiddenColumns] = useState(
    Array.isArray(initial.hiddenColumns) ? initial.hiddenColumns : defaultHiddenColumns,
  );
  const [pageSize, setPageSize] = useState(
    pageSizeOptions.includes(initial.pageSize) ? initial.pageSize : defaultPageSize,
  );
  const [ready, setReady] = useState(false);
  const [canSave, setCanSave] = useState(false);
  // Ostatnio zsynchronizowana z serwerem wartość (null = serwer nie ma jeszcze zapisu,
  // wtedy pierwszy zapis wysyła dotychczasowe ustawienia lokalne).
  const synced = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/me/dashboard-preferences`, {
          headers: createAuthHeaders(),
          params: { view },
        });
        if (!mounted) return;
        const hasColumns = Array.isArray(data.hiddenColumns);
        const hasPageSize = pageSizeOptions.includes(data.pageSize);
        if (hasColumns) setHiddenColumns(data.hiddenColumns);
        if (hasPageSize) setPageSize(data.pageSize);
        synced.current =
          hasColumns && hasPageSize
            ? JSON.stringify({ hiddenColumns: data.hiddenColumns, pageSize: data.pageSize })
            : null;
        setCanSave(true);
      } catch (error) {
        console.error("Nie udało się pobrać preferencji widoku:", error);
      } finally {
        if (mounted) setReady(true);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Cache lokalny - zawsze, niezależnie od serwera.
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ hiddenColumns, pageSize }));
    } catch (e) {
      // brak miejsca / tryb prywatny - widok po prostu nie zostanie zapamiętany lokalnie
    }
  }, [storageKey, hiddenColumns, pageSize]);

  // Zapis na serwerze z opóźnieniem (bez requestu przy każdym kliknięciu).
  useEffect(() => {
    if (!canSave) return undefined;
    const serialized = JSON.stringify({ hiddenColumns, pageSize });
    if (serialized === synced.current) return undefined;
    const timeout = setTimeout(async () => {
      try {
        await axios.put(
          `${API_BASE_URL}/me/dashboard-preferences`,
          { view, hiddenColumns, pageSize },
          { headers: createAuthHeaders() },
        );
        synced.current = serialized;
      } catch (error) {
        console.error("Nie udało się zapisać preferencji widoku:", error);
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [canSave, view, hiddenColumns, pageSize]);

  const toggleColumn = useCallback(
    (id) =>
      setHiddenColumns((prev) =>
        prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
      ),
    [],
  );

  // "Przywróć domyślne" pokazuje wszystkie kolumny.
  const resetColumns = useCallback(() => setHiddenColumns([]), []);

  return {
    hiddenColumns,
    pageSize,
    setPageSize,
    toggleColumn,
    resetColumns,
    ready,
  };
}

export default useViewPreferences;
